import { useEffect, useState } from "react";
import { Select } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import NewTabElements from "../../../../../components/newTabElements/newTabElements";
import {
  CsvConfigData,
  NarCsvConfigService,
} from "../../../../../../shared/api/services/narCsvConfigService";
import {
  NarPdfConfigService,
  PdfConfigData,
} from "../../../../../../shared/api/services/narPdfConfigService";
import useFetchPayerSubtypes from "../hooks/fetchPayerSubtypes";
import {
  extractInsuranceOptions,
  extractPlanOptions,
} from "../utils/extractPayerOptions";
import CsvConfigDisplay from "./csvConfigDisplay";
import PdfConfigDisplay from "./pdfConfigDisplay";

const CsvAndPdfTab = () => {
  const [selectedInsurance, setSelectedInsurance] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [selectedConfigType, setSelectedConfigType] = useState<"csv" | "pdf">(
    "csv",
  );
  const { data: payerSubtypesData, loading: payerSubtypesLoading } =
    useFetchPayerSubtypes();
  const [csvConfigData, setCsvConfigData] = useState<CsvConfigData | null>(
    null,
  );
  const [pdfConfigData, setPdfConfigData] = useState<PdfConfigData | null>(
    null,
  );
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Extract insurance and plan options using utility functions
  const insuranceOptions = extractInsuranceOptions(payerSubtypesData);
  const planOptions = extractPlanOptions(payerSubtypesData, selectedInsurance);

  // Clear plan when insurance changes
  useEffect(() => {
    setSelectedPlan("");
  }, [selectedInsurance]);

  // Fetch data when insurance and plan are selected
  useEffect(() => {
    if (selectedInsurance && selectedPlan) {
      fetchCsvConfig();
      fetchPdfConfig();
    } else {
      setCsvConfigData(null);
      setPdfConfigData(null);
      setCsvError(null);
      setPdfError(null);
    }
  }, [selectedInsurance, selectedPlan]);

  const fetchCsvConfig = async () => {
    if (!selectedInsurance || !selectedPlan) return;

    setCsvLoading(true);
    setCsvError(null);
    try {
      const data = await NarCsvConfigService.getCsvConfig(
        selectedInsurance,
        selectedPlan,
      );
      setCsvConfigData(data);
    } catch (error) {
      console.error("Error fetching CSV config:", error);
      setCsvError(
        error instanceof Error ? error.message : "Failed to fetch CSV config",
      );
      setCsvConfigData(null);
    } finally {
      setCsvLoading(false);
    }
  };

  const fetchPdfConfig = async () => {
    if (!selectedInsurance || !selectedPlan) return;

    setPdfLoading(true);
    setPdfError(null);
    try {
      const data = await NarPdfConfigService.getPdfConfig(
        selectedInsurance,
        selectedPlan,
      );
      setPdfConfigData(data);
    } catch (error) {
      console.error("Error fetching PDF config:", error);
      setPdfError(
        error instanceof Error ? error.message : "Failed to fetch PDF config",
      );
      setPdfConfigData(null);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-4">
      {payerSubtypesLoading ? (
        <div className="flex h-full w-full flex-row items-center justify-center gap-2">
          <LoaderMessage message="Loading..." />
        </div>
      ) : (
        <>
          <div className="flex w-full flex-row gap-4">
            <div className="w-1/2">
              <Select
                id="insurance-select"
                label="Select the Insurance"
                placeholder="Select the Insurance"
                options={insuranceOptions}
                onOptionChange={(data) => {
                  setSelectedInsurance(data.value);
                  setCsvConfigData(null);
                  setPdfConfigData(null);
                }}
                defaultValue={selectedInsurance}
              />
            </div>
            <div className="w-1/2">
              <Select
                key={selectedInsurance}
                id="plan-select"
                label="Select the Plan"
                placeholder="Select the Insurance first to see the plans"
                options={planOptions}
                onOptionChange={(data) => {
                  setSelectedPlan(data.value);
                  setCsvConfigData(null);
                  setPdfConfigData(null);
                }}
                defaultValue={selectedPlan || undefined}
              />
            </div>
          </div>

          {selectedInsurance && selectedPlan && (
            <div className="flex h-full w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4">
              <div className="tabs-container flex flex-shrink-0 gap-2 border-b border-primaryGray-16">
                <NewTabElements
                  tabs={[
                    { id: "csv", label: "CSV Config" },
                    { id: "pdf", label: "PDF Config" },
                  ]}
                  activeTab={selectedConfigType}
                  onTabChange={(tab) =>
                    setSelectedConfigType(tab as "csv" | "pdf")
                  }
                />
              </div>
              <div className="flex-1 overflow-y-auto">
                {selectedConfigType === "csv" ? (
                  <CsvConfigDisplay
                    csvConfigData={csvConfigData}
                    csvLoading={csvLoading}
                    csvError={csvError}
                    payerId={selectedInsurance}
                    subcollection={selectedPlan}
                    onConfigUpdate={() => {
                      fetchCsvConfig();
                    }}
                    onRetry={() => {
                      fetchCsvConfig();
                    }}
                  />
                ) : (
                  <PdfConfigDisplay
                    pdfConfigData={pdfConfigData}
                    pdfLoading={pdfLoading}
                    pdfError={pdfError}
                    payerId={selectedInsurance}
                    subcollection={selectedPlan}
                    onConfigUpdate={() => {
                      fetchPdfConfig();
                    }}
                    onRetry={() => {
                      fetchPdfConfig();
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CsvAndPdfTab;
